<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ref, watch, watchEffect } from 'vue'

// Express mode starts in editing mode and doesn't have a cancel button
// Headless mode doesn't have any buttons, and delegates edit mode to edit prop
const props = withDefaults(
  defineProps<{
    placeholder: string
    cacheValue?: string
    title?: string
    express?: boolean
    headless?: boolean
    edit?: boolean
  }>(),
  {
    title: '',
    cacheValue: '',
    express: false,
    headless: false,
    edit: false,
  }
)

const emits = defineEmits(['save'])

const editing = ref(props.express)

const text = defineModel<string>()

watchEffect(() => {
  text.value = props.cacheValue
})

const enterEditMode = () => {
  editing.value = true
}

const saveNotes = () => {
  editing.value = false
  emits('save', text.value, discardNotes)
}

const discardNotes = () => {
  editing.value = false
  text.value = props.cacheValue
}

watch(
  () => props.edit,
  () => {
    if (!props.edit) emits('save', text.value, discardNotes)
  }
)
</script>

<template>
  <div class="w-full">
    <div class="flex justify-between self-end">
      <el-text class="text-text-tertiary self-center">{{ title }}</el-text>
      <div>
        <el-button
          v-if="!headless && !editing && !express"
          class="border-none bg-transparent p-2"
          title="Edit"
          @click="enterEditMode"
        >
          <Icon icon="material-symbols:edit-outline" />
        </el-button>
        <el-button
          v-if="!headless && editing && !express"
          class="border-none bg-transparent p-2"
          title="Discard"
          @click="discardNotes"
        >
          <Icon icon="iconoir:cancel" />
        </el-button>
        <el-button v-if="!headless && editing" class="border-none bg-transparent p-2" title="Save" @click="saveNotes">
          <Icon icon="material-symbols:check" />
        </el-button>
      </div>
    </div>
    <el-input
      v-model="text"
      :disabled="(!headless && !editing) || (headless && !edit)"
      style="width: 100%"
      :rows="3"
      type="textarea"
      :placeholder="props.placeholder"
    />
  </div>
</template>

<style scoped lang="scss">
:deep() {
  .el-textarea.is-disabled .el-textarea__inner {
    resize: none;
    cursor: default;
  }
}
</style>
